import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import LoanApplication from "@/pages/LoanApplication";
import ClientLogin from "@/pages/ClientLogin";
import ResetPassword from "@/pages/ResetPassword";
import ClientDashboard from "@/pages/ClientDashboard";
import ClientProfile from "@/pages/ClientProfile";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AgentDashboard from "@/pages/AgentDashboard";
import InstallApp from "@/pages/InstallApp";
import { AuthProvider, useAuth } from "@/context/AuthContext";

// Remove Emergent badge
(function hideBadge() {
  const remove = () => { const el = document.getElementById('emergent-badge'); if (el) el.remove(); };
  remove();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', remove);
  const obs = new MutationObserver(() => { remove(); });
  obs.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => obs.disconnect(), 5000);
})();

// Protected route wrapper
const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, isLoading } = useAuth();
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to={requiredRole === 'admin' ? '/admin/login' : '/client/login'} replace />;
  }
  
  if (requiredRole === 'admin') {
    // Admin area is open to all internal staff roles; only clients and agents are redirected
    if (user.role === 'client') return <Navigate to="/client/dashboard" replace />;
    if (user.role === 'sales_agent') return <Navigate to="/agent/dashboard" replace />;
  } else if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/apply" element={<LoanApplication />} />
            <Route path="/client/login" element={<ClientLogin />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            
            {/* Protected Client Routes */}
            <Route
              path="/client/dashboard"
              element={
                <ProtectedRoute requiredRole="client">
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/profile"
              element={
                <ProtectedRoute requiredRole="client">
                  <ClientProfile />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Agent Routes */}
            <Route path="/install" element={<InstallApp />} />
            <Route
              path="/agent/dashboard"
              element={
                <ProtectedRoute requiredRole="sales_agent">
                  <AgentDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Protected Admin Routes */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            
            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;
